"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.trackEmbeddedVideo = exports.trackHtmlVideo = void 0;
var tslib_1 = require("tslib");
function calculatePercentCompleted(currentTime, duration) {
    var percentCompleted = 0;
    if (Number.isFinite(currentTime) && Number.isFinite(duration) && duration > 0) {
        var rawPercent = (currentTime / duration) * 100;
        percentCompleted = Math.min(100, Math.max(0, rawPercent));
    }
    return percentCompleted;
}
function getVideoData(videoEl, stopReason) {
    var currentTime = videoEl.currentTime;
    var duration = videoEl.duration;
    return tslib_1.__assign({ duration: duration, start_time: currentTime, last_position: currentTime, percent_completed: calculatePercentCompleted(currentTime, duration) }, (stopReason !== undefined ? { stop_reason: stopReason } : {}));
}
function getMuxMetadata(videoEl) {
    return {
        mux_playback_id: videoEl.getAttribute('playback-id'),
        mux_video_id: videoEl.getAttribute('metadata-video-id'),
        mux_video_title: videoEl.getAttribute('metadata-video-title'),
    };
}
/**
 * Track a standard HTML video element.
 *
 * @param videoEl - The HTML video element to track.
 * @param handlers - The video handlers to call when on video lifecycle events.
 * @returns A function to untrack the video.
 */
function trackHtmlVideo(videoEl, handlers, vendor) {
    var playHandler = function () {
        var startEvent = tslib_1.__assign(tslib_1.__assign({}, getVideoData(videoEl)), (vendor === 'mux' ? getMuxMetadata(videoEl) : {}));
        handlers.onPlay(startEvent);
    };
    videoEl.addEventListener('play', playHandler);
    var pauseHandler = function () {
        var pauseEvent = tslib_1.__assign(tslib_1.__assign({}, getVideoData(videoEl, 'paused')), (vendor === 'mux' ? getMuxMetadata(videoEl) : {}));
        handlers.onPause(pauseEvent);
    };
    videoEl.addEventListener('pause', pauseHandler);
    var endedHandler = function () {
        var endedEvent = tslib_1.__assign(tslib_1.__assign({}, getVideoData(videoEl, 'ended')), (vendor === 'mux' ? getMuxMetadata(videoEl) : {}));
        handlers.onEnded(endedEvent);
    };
    videoEl.addEventListener('ended', endedHandler);
    var seekingHandler = function () {
        var seekingEvent = tslib_1.__assign(tslib_1.__assign({}, getVideoData(videoEl, 'seeking')), (vendor === 'mux' ? getMuxMetadata(videoEl) : {}));
        handlers.onSeeking(seekingEvent);
    };
    videoEl.addEventListener('seeking', seekingHandler);
    var seekedHandler = function () {
        var seekedEvent = tslib_1.__assign(tslib_1.__assign({}, getVideoData(videoEl)), (vendor === 'mux' ? getMuxMetadata(videoEl) : {}));
        handlers.onSeeked(seekedEvent);
    };
    videoEl.addEventListener('seeked', seekedHandler);
    var timeupdateHandler = function () {
        var media = videoEl;
        var timeupdateEvent = {
            position: videoEl.currentTime,
            isSeeking: !!media.seeking,
        };
        handlers.onTimeUpdate(timeupdateEvent);
    };
    videoEl.addEventListener('timeupdate', timeupdateHandler);
    return function () {
        videoEl.removeEventListener('play', playHandler);
        videoEl.removeEventListener('pause', pauseHandler);
        videoEl.removeEventListener('ended', endedHandler);
        videoEl.removeEventListener('seeking', seekingHandler);
        videoEl.removeEventListener('seeked', seekedHandler);
        videoEl.removeEventListener('timeupdate', timeupdateHandler);
    };
}
exports.trackHtmlVideo = trackHtmlVideo;
function getTimeUpdateInfo(player) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var currentTime;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, new Promise(function (resolve) { return player.getCurrentTime(resolve); })];
                case 1:
                    currentTime = _a.sent();
                    return [2 /*return*/, { currentTime: currentTime }];
            }
        });
    });
}
function getIframeMetadata(player, elem, vendor, stopReason) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var _a, duration, currentTime, vendorMetadata, url;
        return tslib_1.__generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, Promise.all([
                        new Promise(function (resolve) { return player.getDuration(resolve); }),
                        new Promise(function (resolve) { return player.getCurrentTime(resolve); }),
                    ])];
                case 1:
                    _a = tslib_1.__read.apply(void 0, [_b.sent(), 2]), duration = _a[0], currentTime = _a[1];
                    vendorMetadata = {};
                    if (vendor === 'mux') {
                        url = void 0;
                        try {
                            url = new URL(elem.getAttribute('src'));
                            vendorMetadata.mux_video_title = url.searchParams.get('metadata-video-title');
                            vendorMetadata.mux_video_id = url.searchParams.get('metadata-video-id');
                            vendorMetadata.mux_playback_id = url.pathname.split('/').pop();
                        }
                        catch (error) {
                            // invalid or no src url, skip the header metadata
                        }
                    }
                    return [2 /*return*/, tslib_1.__assign(tslib_1.__assign({ duration: duration, start_time: currentTime, last_position: currentTime, percent_completed: calculatePercentCompleted(currentTime, duration) }, (stopReason !== undefined ? { stop_reason: stopReason } : {})), vendorMetadata)];
            }
        });
    });
}
function trackEmbeddedVideo(player, handlers, vendor) {
    if (vendor === void 0) { vendor = null; }
    var onUnsubscribe = [];
    var readyHandler = function () {
        var elem = player.elem;
        var isSeeking = false;
        var playHandler = function () {
            getIframeMetadata(player, elem, vendor)
                .then(function (playerState) {
                handlers.onPlay(playerState);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'play' handler: ".concat(error));
            });
        };
        player.on('play', playHandler);
        onUnsubscribe.push(function () { return player.off('play', playHandler); });
        var pauseHandler = function () {
            getIframeMetadata(player, elem, vendor, 'paused')
                .then(function (playerState) {
                handlers.onPause(playerState);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'pause' handler: ".concat(error));
            });
        };
        player.on('pause', pauseHandler);
        onUnsubscribe.push(function () { return player.off('pause', pauseHandler); });
        var endedHandler = function () {
            getIframeMetadata(player, elem, vendor, 'ended')
                .then(function (playerState) {
                handlers.onEnded(playerState);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'ended' handler: ".concat(error));
            });
        };
        player.on('ended', endedHandler);
        onUnsubscribe.push(function () { return player.off('ended', endedHandler); });
        var seekingHandler = function () {
            isSeeking = true;
            getIframeMetadata(player, elem, vendor, 'seeking')
                .then(function (playerState) {
                handlers.onSeeking(playerState);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'seeking' handler: ".concat(error));
            });
        };
        player.on('seeking', seekingHandler);
        onUnsubscribe.push(function () { return player.off('seeking', seekingHandler); });
        var seekedHandler = function () {
            isSeeking = false;
            getIframeMetadata(player, elem, vendor)
                .then(function (playerState) {
                handlers.onSeeked(playerState);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'seeked' handler: ".concat(error));
            });
        };
        player.on('seeked', seekedHandler);
        onUnsubscribe.push(function () { return player.off('seeked', seekedHandler); });
        var timeupdateHandler = function () {
            getTimeUpdateInfo(player)
                .then(function (_a) {
                var currentTime = _a.currentTime;
                var timeupdateEvent = {
                    position: currentTime,
                    isSeeking: isSeeking,
                };
                handlers.onTimeUpdate(timeupdateEvent);
            })
                .catch(function (error) {
                handlers.onError("Error getting iframe metadata from 'timeupdate' handler: ".concat(error));
            });
        };
        player.on('timeupdate', timeupdateHandler);
        onUnsubscribe.push(function () { return player.off('timeupdate', timeupdateHandler); });
    };
    player.on('ready', readyHandler);
    return function () {
        player.off('ready', readyHandler);
        onUnsubscribe.forEach(function (unsubscribe) { return unsubscribe(); });
    };
}
exports.trackEmbeddedVideo = trackEmbeddedVideo;
//# sourceMappingURL=track-video.js.map